import { apiService } from '@/services/api'

// Note: 파트너 인증은 HttpOnly 쿠키 기반으로 작동합니다.
// apiService는 withCredentials: true로 설정되어 있어 자동으로 쿠키를 전송합니다.

// 파트너 인증 관련
export interface PartnerLoginRequest {
  username: string
  password: string
}

export interface PartnerResponse {
  id: number
  username: string
  name: string
  email?: string
  phone?: string
  company?: string
  is_active: boolean
  can_view_users: boolean
  can_view_trading: boolean
  can_view_deposits: boolean
  can_view_withdrawals: boolean
  can_view_fees: boolean
  can_view_statistics: boolean
  created_at: string
  last_login_at?: string
  referral_code?: string
  commission_rate?: number
}

export interface PartnerLoginResponse {
  access_token: string
  token_type: string
  partner: PartnerResponse
}

// 대시보드 관련 (새로운 단순화된 시스템)
export interface PartnerDashboardResponse {
  partner_info: {
    id: number
    name: string
    username: string
    referral_code: string
    commission_rate: number
    settlement_mode?: string  // DEPOSIT=죽장 / PNL=손익 (2026-07-24)
    parent_partner_id?: number | null
    partner_level?: number
    sub_partner_count?: number
    created_at: string
  }
  stats: {
    // 직접 추천 유저 통계
    direct_users_count: number
    active_users_count: number

    // 유저 재정 현황
    total_user_deposits: number
    total_user_withdrawals: number
    total_user_balance: number
    direct_user_net_loss: number

    // 하위 파트너 유저 통계
    sub_partner_user_count: number
    sub_partner_net_loss: number
    sub_partner_earnings: number

    // 수익 현황
    direct_gross_earnings: number
    total_gross_earnings: number
    total_withdrawn: number
    available_balance: number
  }
  user_stats: Array<{
    user_id: number
    email: string
    nickname: string
    total_deposited: number
    total_withdrawn: number
    current_balance: number
    net_loss: number
  }>
}

// 사용자 관련
export interface PartnerUser {
  id: number
  email: string
  created_at: string
  is_active: boolean
  total_earnings: number
  total_losses: number
  trade_count: number
}

export interface PartnerUsersResponse {
  users: PartnerUser[]
  total: number
  page: number
  pages: number
}

// 거래 내역 관련
export interface PartnerTrade {
  id: number
  user_email: string
  user_nickname: string
  symbol: string
  side: string
  margin_type: string
  margin_amount: number
  leverage: number
  entry_price?: number
  exit_price?: number
  liquidation_price?: number
  pnl?: number
  pnl_percentage?: number
  status: string
  created_at: string
  closed_at?: string
}

export interface PartnerTradingResponse {
  trades: PartnerTrade[]
  total: number
  page: number
  pages: number
}

// 입금 내역 관련
export interface PartnerDeposit {
  id: number
  user_email: string
  user_nickname: string
  user_name: string
  amount_krw: number
  deposit_type: string
  depositor_name: string
  bank_name?: string
  status: string
  created_at: string
  approved_at?: string
  reject_reason?: string
}

export interface PartnerDepositsResponse {
  deposits: PartnerDeposit[]
  total: number
  page: number
  pages: number
}

// 출금 내역 관련
export interface PartnerWithdrawal {
  id: number
  user_email: string
  user_nickname: string
  user_name: string
  amount_usdt: number
  amount_krw: number
  wallet_address: string
  network: string
  status: string
  created_at: string
  approved_at?: string
  rejected_at?: string
  reject_reason?: string
  tx_hash?: string
}

export interface PartnerWithdrawalsResponse {
  withdrawals: PartnerWithdrawal[]
  total: number
  page: number
  pages: number
}

// 수수료 관련
export interface PartnerFee {
  id: number
  user_email: string
  user_nickname: string
  user_name: string
  symbol: string
  side: string
  user_loss: number
  commission_rate: number
  partner_earnings: number
  created_at: string
  position_id: number
}

export interface PartnerFeesResponse {
  fees: PartnerFee[]
  total: number
  page: number
  pages: number
}

export interface PartnerFeesSummary {
  total_earnings: number
  total_user_losses: number
  total_transactions: number
  monthly_earnings: number
  average_commission_rate: number
  partner_referral_code: string
}

// 수익 내역 관련
export interface PartnerEarning {
  id: number
  user_email: string
  user_id: number
  trade_id: number
  symbol: string
  trade_type?: string
  side?: string
  position_type?: string
  position_size: number
  entry_price: number
  exit_price: number
  user_pnl: number
  commission_rate: number
  commission_amount?: number
  partner_earnings?: number
  action_type: string  // OPEN, FULL_CLOSE, PARTIAL_CLOSE
  created_at: string
  closed_at?: string
}

export interface PartnerEarningsResponse {
  earnings: PartnerEarning[]
  fees?: PartnerEarning[]  // 호환성을 위한 alternative key
  total: number
  page: number
  limit: number
  pages: number
  total_commission: number
}

// 파트너 출금 관련
export interface PartnerWithdrawalRequest {
  amount: string
  account_info: string
  account_type: string
  memo?: string
}

export interface PartnerBalanceResponse {
  commission_rate: number
  total_gross_earnings: number
  total_withdrawn: number
  available_balance: number
  can_withdraw: boolean
  direct_users_count: number
  total_user_deposits: number
  total_user_withdrawals: number
  total_user_balance: number
  direct_user_net_loss: number
}

export interface PartnerWithdrawalHistoryItem {
  id: number
  partner_id: number
  amount: string
  account_info: string
  account_type: string
  memo?: string
  status: string
  admin_memo?: string
  processed_by_admin_id?: number
  processed_at?: string
  created_at: string
  updated_at: string
}

class PartnerApiService {
  // 인증
  async login(credentials: PartnerLoginRequest): Promise<PartnerLoginResponse> {
    // 직접 axios를 사용해서 raw response를 받습니다
    const axiosResponse = await apiService.getClient().post<PartnerLoginResponse>('/api/v1/partners/auth/login', credentials)
    const loginResponse = axiosResponse.data
    
    console.log('Raw partner login response:', loginResponse)
    
    return loginResponse
  }

  async logout(): Promise<void> {
    await apiService.post('/api/v1/partners/auth/logout')
  }

  async getProfile(): Promise<PartnerResponse> {
    // 직접 axios를 사용해서 raw response를 받습니다
    const axiosResponse = await apiService.getClient().get<PartnerResponse>('/api/v1/partners/auth/me')
    return axiosResponse.data
  }

  async refreshToken(): Promise<PartnerLoginResponse> {
    // 직접 axios를 사용해서 raw response를 받습니다
    const axiosResponse = await apiService.getClient().post<PartnerLoginResponse>('/api/v1/partners/auth/refresh')
    return axiosResponse.data
  }

  // 대시보드
  async getDashboard(): Promise<PartnerDashboardResponse> {
    const resp = await apiService.get<any>('/api/v1/partners/dashboard')
    return (resp as any) as PartnerDashboardResponse
  }

  // 사용자 목록
  // 산하 회원 실시간 현황 (2026-08-31) — NF 패스스루 파트너 엔드포인트
  async getRealtime(): Promise<any> {
    const resp = await apiService.get<any>('/api/v1/nf/partner/realtime')
    return (resp as any).data || resp
  }

  async getStatistics(range = '30d'): Promise<any> {
    const resp = await apiService.get<any>(`/api/v1/partners/statistics?range=${range}`)
    return (resp as any)
  }

  async getUsers(page = 1, limit = 20, keyword?: string, status?: string): Promise<PartnerUsersResponse> {
    const params = new URLSearchParams({ 
      page: String(page), 
      limit: String(limit) 
    })
    if (keyword) params.append('keyword', keyword)
    if (status) params.append('status', status)
    
    const resp = await apiService.get<any>(`/api/v1/partners/users?${params}`)
    return (resp as any) as PartnerUsersResponse
  }

  // 거래 내역
  async getTrading(page = 1, limit = 20, symbol?: string, side?: string, status?: string, search?: string): Promise<PartnerTradingResponse> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit)
    })
    if (symbol) params.append('symbol', symbol)
    if (side) params.append('side', side)
    if (status) params.append('status', status)
    if (search) params.append('search', search)

    const resp = await apiService.get<any>(`/api/v1/partners/trading?${params}`)
    return (resp as any) as PartnerTradingResponse
  }

  // 거래 심볼 목록
  async getTradingSymbols(): Promise<{ symbols: string[]; total: number }> {
    const resp = await apiService.get<any>('/api/v1/partners/trading/symbols')
    return (resp as any) as { symbols: string[]; total: number }
  }

  // 입금 내역
  async getDeposits(page = 1, limit = 20, status?: string): Promise<PartnerDepositsResponse> {
    const params = new URLSearchParams({ 
      page: String(page), 
      limit: String(limit) 
    })
    if (status) params.append('status', status)
    
    const resp = await apiService.get<any>(`/api/v1/partners/deposits?${params}`)
    return (resp as any) as PartnerDepositsResponse
  }

  // 출금 내역
  async getWithdrawals(page = 1, limit = 20, status?: string): Promise<PartnerWithdrawalsResponse> {
    const params = new URLSearchParams({ 
      page: String(page), 
      limit: String(limit) 
    })
    if (status) params.append('status', status)
    
    const resp = await apiService.get<any>(`/api/v1/partners/withdrawals?${params}`)
    return (resp as any) as PartnerWithdrawalsResponse
  }

  // 수수료/수익
  async getFees(page = 1, limit = 20, symbol?: string, side?: string): Promise<PartnerFeesResponse> {
    console.log('partnerApiService.getFees called', { page, limit, symbol, side })
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit)
    })
    if (symbol) params.append('symbol', symbol)
    if (side) params.append('side', side)

    const resp = await apiService.get<any>(`/api/v1/partners/fees?${params}`)
    console.log('partnerApiService.getFees response:', resp)
    return (resp as any) as PartnerFeesResponse
  }

  async getFeesSymbols(): Promise<{ symbols: string[]; total: number }> {
    const resp = await apiService.get<any>('/api/v1/partners/fees/symbols')
    return (resp as any) as { symbols: string[]; total: number }
  }

  async getFeesSummary(): Promise<PartnerFeesSummary> {
    const resp = await apiService.get<any>('/api/v1/partners/fees/summary')
    return (resp as any) as PartnerFeesSummary
  }

  // 파트너 출금
  async getBalance(): Promise<PartnerBalanceResponse> {
    const resp = await apiService.get<any>('/api/v1/partners/withdrawals/balance')
    return (resp as any) as PartnerBalanceResponse
  }

  async requestWithdrawal(request: PartnerWithdrawalRequest): Promise<{ result: string; message: string }> {
    const resp = await apiService.post<any>('/api/v1/partners/withdrawals/request', request)
    return (resp as any) as { result: string; message: string }
  }

  async getWithdrawalHistory(limit = 50, offset = 0): Promise<PartnerWithdrawalHistoryItem[]> {
    const params = new URLSearchParams({ 
      limit: String(limit), 
      offset: String(offset) 
    })
    
    const resp = await apiService.get<any>(`/api/v1/partners/withdrawals/history?${params}`)
    return (resp as any) as PartnerWithdrawalHistoryItem[]
  }

  async getWithdrawalDetail(id: number): Promise<PartnerWithdrawalHistoryItem> {
    const resp = await apiService.get<any>(`/api/v1/partners/withdrawals/${id}`)
    return (resp as any) as PartnerWithdrawalHistoryItem
  }

  // 수익 내역 (상세)
  async getEarnings(page = 1, limit = 20, search?: string, dateFilter?: string, symbolFilter?: string): Promise<PartnerEarningsResponse> {
    const params = new URLSearchParams({ 
      page: String(page), 
      limit: String(limit) 
    })
    if (search) params.append('search', search)
    if (dateFilter && dateFilter !== 'all') params.append('date_filter', dateFilter)
    if (symbolFilter && symbolFilter !== 'all') params.append('symbol', symbolFilter)
    
    const resp = await apiService.get<any>(`/api/v1/partners/fees?${params}`)
    return (resp as any) as PartnerEarningsResponse
  }

  // 문의 관련
  async getInquiries(page = 1, limit = 20, status?: string): Promise<any> {
    console.log('partnerApiService.getInquiries called', { page, limit, status })
    const params = new URLSearchParams({ 
      page: String(page), 
      limit: String(limit) 
    })
    if (status) params.append('status', status)
    
    const resp = await apiService.get<any>(`/api/v1/partners/inquiries?${params}`)
    console.log('partnerApiService.getInquiries response:', resp)
    return (resp as any)
  }

  async getInquiryCategories(): Promise<any> {
    console.log('partnerApiService.getInquiryCategories called')
    const resp = await apiService.get<any>('/api/v1/partners/inquiries/categories')
    console.log('partnerApiService.getInquiryCategories response:', resp)
    return (resp as any)
  }

  async createInquiry(inquiry: { title: string; content: string; category?: string; priority: string }): Promise<any> {
    console.log('partnerApiService.createInquiry called', inquiry)
    const resp = await apiService.post<any>('/api/v1/partners/inquiries', inquiry)
    console.log('partnerApiService.createInquiry response:', resp)
    return (resp as any)
  }
}

export const partnerApiService = new PartnerApiService()
export default partnerApiService